import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Snackbar,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import api from "../../utils/api";
import Table from "../../components/Table";
import { TransactionColumns } from "./components/TransactionColumns";
import TransactionOrderDetailDialog from "./components/TransactionOrderDetailDialog";
import { formatMoney } from "../../utils/helpers";

const STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "created", label: "Created" },
    { value: "processed", label: "Processed" },
    { value: "canceled", label: "Canceled" },
    { value: "refunded", label: "Refunded" },
    { value: "failed", label: "Failed" },
];

export default function Transactions() {
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({
        transaction_id: "",
        order_id: "",
        status: "",
        customer: "",
        from: "",
        to: "",
    });
    const [confirm, setConfirm] = useState(null);
    const [detailRow, setDetailRow] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [feedback, setFeedback] = useState({ open: false, severity: "success", message: "" });

    const columns = useMemo(
        () => TransactionColumns({ onOpenOrder: (row) => setDetailRow(row) }),
        []
    );

    const fetchRows = useCallback(async (nextFilters = filters, nextPage = pagination.page) => {
        setLoading(true);
        try {
            const res = await api.get("admin/transactions", {
                params: {
                    page: nextPage,
                    perPage: pagination.perPage,
                    filter: nextFilters,
                },
            });
            setRows(res.data?.data || []);
            const p = res.data?.meta?.pagination || {};
            setPagination((prev) => ({
                ...prev,
                page: p.page || nextPage,
                total: p.total || 0,
                totalPages: p.totalPages || 0,
            }));
        } catch (err) {
            console.error("Failed to load transactions", err);
            setFeedback({
                open: true,
                severity: "error",
                message: err.response?.data?.message || "Failed to load transactions.",
            });
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.page, pagination.perPage]);

    useEffect(() => {
        fetchRows(filters, 1);
    }, [filters, fetchRows]);

    const downloadCsv = () => {
        const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
        const url = new URL(`${base}/admin/transactions/download`, window.location.origin);
        const token =
            new URLSearchParams(window.location.search).get("token") ||
            localStorage.getItem("sessionID");
        if (token) url.searchParams.set("token", token);
        window.open(url.toString(), "_blank");
    };

    const askConfirm = (action, row) => setConfirm({ action, row });

    const runAction = async () => {
        if (!confirm) return;
        const { action, row } = confirm;
        const endpoint = action === "refund"
            ? `admin/transactions/${row.id}/refund`
            : `admin/transactions/${row.id}/reverse`;
        const label = action === "refund" ? "Refund" : "Cancel";

        setActionLoading(true);
        try {
            const res = await api.post(endpoint);
            setFeedback({
                open: true,
                severity: "success",
                message: res.data?.message || `${label} successful.`,
            });
            setConfirm(null);
            fetchRows(filters, pagination.page);
        } catch (err) {
            setFeedback({
                open: true,
                severity: "error",
                message:
                    err.response?.data?.message ||
                    (typeof err.response?.data === "string" ? err.response.data : null) ||
                    `${label} failed.`,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const resetFilters = () => setFilters({
        transaction_id: "",
        order_id: "",
        status: "",
        customer: "",
        from: "",
        to: "",
    });

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Typography component="h2" variant="h6" sx={{ m: 0 }}>
                    Transactions
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={downloadCsv}>
                        Download CSV
                    </Button>
                </Stack>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gap: 1.5,
                    mb: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                        lg: "repeat(7, minmax(0, 1fr))",
                    },
                }}
            >
                <OutlinedInput
                    size="small"
                    placeholder="Transaction #"
                    value={filters.transaction_id}
                    onChange={(e) => setFilters((p) => ({ ...p, transaction_id: e.target.value }))}
                />
                <OutlinedInput
                    size="small"
                    placeholder="Order #"
                    value={filters.order_id}
                    onChange={(e) => setFilters((p) => ({ ...p, order_id: e.target.value }))}
                />
                <FormControl size="small">
                    <InputLabel id="tx-status-label">Status</InputLabel>
                    <Select
                        labelId="tx-status-label"
                        label="Status"
                        value={filters.status}
                        onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                    >
                        {STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || "all"} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <OutlinedInput
                    size="small"
                    placeholder="Customer"
                    value={filters.customer}
                    onChange={(e) => setFilters((p) => ({ ...p, customer: e.target.value }))}
                />
                <OutlinedInput
                    size="small"
                    type="date"
                    value={filters.from}
                    onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                />
                <OutlinedInput
                    size="small"
                    type="date"
                    value={filters.to}
                    onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                />
                <Button variant="outlined" onClick={resetFilters}>Reset</Button>
            </Box>

            <Table
                loading={loading}
                rows={rows}
                columns={columns}
                selactable={false}
                draggable={false}
                renderActions={(row) => (
                    <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title={row.can_cancel ? "Cancel / Reverse" : "Cancel not available"}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="warning"
                                    disabled={!row.can_cancel}
                                    onClick={() => askConfirm("cancel", row)}
                                    aria-label="Cancel"
                                >
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={row.can_refund ? "Refund" : (row.refund_disabled_reason || "Refund not available")}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    disabled={!row.can_refund}
                                    onClick={() => askConfirm("refund", row)}
                                    aria-label="Refund"
                                >
                                    <ReplayIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                )}
            />

            <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                rowsPerPage={pagination.perPage}
                rowsPerPageOptions={[]}
                onPageChange={(event, newPage) => fetchRows(filters, newPage + 1)}
            />

            <Dialog open={!!confirm} onClose={() => !actionLoading && setConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {confirm?.action === "refund" ? "Refund transaction" : "Cancel / Reverse transaction"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirm?.action === "refund"
                            ? `Refund transaction #${confirm?.row?.id} (${formatMoney(confirm?.row?.amount)})? The amount will be returned to the cardholder, and course access for this order will be revoked (purchase canceled / classroom removed).`
                            : `Reverse transaction #${confirm?.row?.id} (${formatMoney(confirm?.row?.amount)})? This voids an authorisation that has not yet been settled by the bank. Course access for this order will also be revoked.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={actionLoading}>Close</Button>
                    <Button
                        variant="contained"
                        color={confirm?.action === "refund" ? "error" : "warning"}
                        sx={{ color: "#fff" }}
                        onClick={runAction}
                        disabled={actionLoading}
                    >
                        {actionLoading ? "Processing..." : (confirm?.action === "refund" ? "Refund" : "Reverse")}
                    </Button>
                </DialogActions>
            </Dialog>

            <TransactionOrderDetailDialog
                open={!!detailRow}
                transaction={detailRow}
                onClose={() => setDetailRow(null)}
            />

            <Snackbar
                open={feedback.open}
                autoHideDuration={5000}
                onClose={() => setFeedback((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={feedback.severity}
                    onClose={() => setFeedback((p) => ({ ...p, open: false }))}
                    sx={{ width: "100%" }}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
